import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that adds multiple variants to the cart in one request.
 *
 * @typedef {object} QuickAddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @property {HTMLElement} liveRegion - The live region for screen reader announcements.
 * @property {HTMLElement | undefined} addToCartTextError - The error message element.
 * @extends Component<QuickAddToCartRefs>
 */
class QuickAddToCartComponent extends Component {
  requiredRefs = ['addToCartButton', 'liveRegion'];

  /** @type {boolean} */
  #isSubmitting = false;

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #errorTimeout;

  /**
   * Handles the click event for the add to cart button.
   */
  handleSubmit() {
    if (this.#isSubmitting) return;

    const items = /** @type {Array<{id: number, quantity: number}>} */ (this.#getItems());
    if (!items || items.length === 0) return;

    this.#isSubmitting = true;
    this.#animateAddedState();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = /** @type {string[]} */ ([]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const body = /** @type { { items: Array<{id: number, quantity: number}>, sections?: string } } */ ({ items });
    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(',');
    }

    const config = fetchConfig('javascript', { body: JSON.stringify(body) });
    config.headers['Content-Type'] = 'application/json';

    fetch(Theme.routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id || '', response.message, response.description, response.errors),
          );

          const { addToCartTextError } = this.refs;
          if (addToCartTextError) {
            addToCartTextError.removeAttribute('hidden');
            addToCartTextError.textContent = response.message;
          }

          this.#setLiveRegionText(response.message);

          if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
          this.#errorTimeout = setTimeout(() => {
            if (addToCartTextError) {
              addToCartTextError.setAttribute('hidden', '');
            }
            this.#clearLiveRegionText();
          }, 10000);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'quick-add-to-cart-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
              sections: response.sections,
            }),
          );

          return;
        }

        const { addToCartTextError } = this.refs;
        if (addToCartTextError) {
          addToCartTextError.setAttribute('hidden', '');
        }

        if (Theme.translations.added) {
          this.#setLiveRegionText(Theme.translations.added);
          setTimeout(() => {
            this.#clearLiveRegionText();
          }, 5000);
        }

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            source: 'quick-add-to-cart-component',
            itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
            productId: this.dataset.productId,
            sections: response.sections,
          }),
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#isSubmitting = false;
      });
  }

  /**
   * Parses the items from the data-items attribute.
   * @returns {Array<{id: number, quantity: number}> | null}
   */
  #getItems() {
    try {
      return JSON.parse(this.dataset.items || '[]');
    } catch {
      console.error('Invalid items JSON:', this.dataset.items);
      return null;
    }
  }

  /**
   * @param {string} text
   */
  #setLiveRegionText(text) {
    this.refs.liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    this.refs.liveRegion.textContent = '';
  }

  /**
   * Animates the "Added" state on the button.
   */
  #animateAddedState() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);

    addToCartButton.setAttribute('data-added', '');

    this.#animationTimeout = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('quick-add-to-cart-component')) {
  customElements.define('quick-add-to-cart-component', QuickAddToCartComponent);
}
